import { useCallback, useEffect, useRef, useState } from "react";
import { ISensorMeasuredData } from "types/ISensorMeasuredData";
import { SensorAlertConfig } from "types/ISensorAlertConfig";
import { SensorAlertState } from "../types";

interface UseSensorAlertSectionParams {
  sensors: ISensorMeasuredData[];
  suggestedSensors: ISensorMeasuredData[];
  isLoading: boolean;
  hasError: boolean;
  selectedSensorIds: number[];
  onSelectSensors: (ids: number[]) => void;
  /**
   * When editing an existing event, pass the persisted sensorAlertConfig from
   * form initial values. The hook will transition directly to CONFIGURATION
   * state and defer the onSelectSensors sync until sensor data has loaded,
   * preventing the form from being overwritten with empty sensor selections
   * before the sensor list is available (FR-027, FR-029).
   */
  initialSensorAlertConfig?: SensorAlertConfig;
}

interface UseSensorAlertSectionResult {
  state: SensorAlertState;
  startConfiguration: (sensorIds: number[]) => void;
  disableAlert: () => void;
  resetToInitial: () => void;
}

export function useSensorAlertSection({
  sensors,
  suggestedSensors,
  isLoading,
  hasError,
  selectedSensorIds,
  onSelectSensors,
  initialSensorAlertConfig,
}: UseSensorAlertSectionParams): UseSensorAlertSectionResult {
  // In edit mode, start in CONFIGURATION directly if initial config has sensors
  const hasInitialConfig =
    (initialSensorAlertConfig?.sensors?.length ?? 0) > 0;

  const [state, setState] = useState<SensorAlertState>(
    hasInitialConfig ? SensorAlertState.CONFIGURATION : SensorAlertState.INITIAL,
  );
  // Track last synced sensor IDs to avoid redundant onSelectSensors calls
  const lastSyncedIdsRef = useRef<string>("");

  // Determine initial state based on loaded sensors
  useEffect(() => {
    if (isLoading || hasError) return;

    // If sensors are already selected (from FC return or edit mode), go to configuration
    if (selectedSensorIds.length > 0) {
      const key = selectedSensorIds.slice().sort().join(",");
      if (lastSyncedIdsRef.current !== key) {
        // Defer onSelectSensors until sensor list has loaded.
        // In edit mode the form values (sensorAlertConfig.sensors with names) are
        // already set by useEventEditDrawer — calling onSelectSensors with an empty
        // sensors array would overwrite them with empty selections (FR-027).
        if (sensors.length > 0) {
          lastSyncedIdsRef.current = key;
          onSelectSensors(selectedSensorIds);
        }
        // else: sensors not yet loaded — keep existing form values, retry on next run
      }
      setState(SensorAlertState.CONFIGURATION);
      return;
    }

    lastSyncedIdsRef.current = "";

    // No sensors in the macro region
    if (sensors.length === 0) {
      setState(SensorAlertState.EMPTY);
      return;
    }

    // Sensors exist → initial state (suggestion or manual)
    setState(SensorAlertState.INITIAL);
  }, [
    sensors,
    isLoading,
    hasError,
    selectedSensorIds,
    suggestedSensors.length,
    onSelectSensors,
  ]);

  const startConfiguration = useCallback(
    (sensorIds: number[]) => {
      onSelectSensors(sensorIds);
      setState(SensorAlertState.CONFIGURATION);
    },
    [onSelectSensors],
  );

  const disableAlert = useCallback(() => {
    onSelectSensors([]);
    setState(SensorAlertState.INITIAL);
  }, [onSelectSensors]);

  const resetToInitial = useCallback(() => {
    onSelectSensors([]);
    setState(SensorAlertState.INITIAL);
  }, [onSelectSensors]);

  return {
    state,
    startConfiguration,
    disableAlert,
    resetToInitial,
  };
}
